import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import eventsData from '../data/events.json';
import '../styles/EventDetail.css';
import foot from '../assets/foot.webp';
import crick from '../assets/crick.webp';
import basket from '../assets/basket.webp';
import valley from '../assets/valley.webp';
import tenis from '../assets/tenis.webp';
import badmin from '../assets/badmin.webp';

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const eventImages = {
    "foot.webp": foot,
    "crick.webp": crick,
    "basket.webp": basket,
    "valley.webp": valley,
    "tenis.webp": tenis,
    "badmin.webp": badmin
  };

  useEffect(() => {
    const foundEvent = eventsData.events.find(e => e.id === parseInt(id));
    setEvent(foundEvent);
    setLoading(false);
  }, [id]);

  const handleRegister = () => {
    navigate(`/register/${event.id}`);
  };

  const goBack = () => {
    navigate(-1);
  };

  // Helper function
  const getBookedCount = (bookedTeams) => {
    if (typeof bookedTeams === 'number') return bookedTeams;
    return 0;
  };

  const isBookingOpen = (bookedTeams) => {
    return typeof bookedTeams === 'number';
  };

  const getSlotText = (bookedTeams, maxTeams) => {
    if (typeof bookedTeams === 'string') {
      return bookedTeams;
    }
    const available = maxTeams - bookedTeams;
    if (available === 0) return '🔴 Fully Booked';
    return `${bookedTeams}/${maxTeams} teams booked • ${available} slots left`;
  };

  const getProgress = (bookedTeams, maxTeams) => {
    if (typeof bookedTeams !== 'number') return 0;
    return (bookedTeams / maxTeams) * 100;
  };

  if (loading) {
    return (
      <div className="detail-page-wrapper">
        <div className="detail-bg-glow detail-bg-glow-1"></div>
        <div className="detail-bg-glow detail-bg-glow-2"></div>
        <div className="detail-container">
          <div className="detail-loading">
            <div className="detail-loader"></div>
            <p>Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="detail-page-wrapper">
        <div className="detail-bg-glow detail-bg-glow-1"></div>
        <div className="detail-bg-glow detail-bg-glow-2"></div>
        <div className="detail-container">
          <div className="detail-not-found">
            <span className="detail-not-found-icon">😕</span>
            <h2>Event Not Found</h2>
            <p>The event you're looking for doesn't exist.</p>
            <Link to="/events" className="detail-not-found-btn">← Back to Events</Link>
          </div>
        </div>
      </div>
    );
  }

  const bookedCount = getBookedCount(event.bookedTeams);
  const bookingOpen = isBookingOpen(event.bookedTeams);
  const progress = getProgress(event.bookedTeams, event.maxTeams);
  const isFull = bookingOpen && bookedCount === event.maxTeams;
  const slotText = getSlotText(event.bookedTeams, event.maxTeams);

  const getStatusClass = (status) => {
    switch(status) {
      case 'upcoming': return 'status-upcoming';
      case 'planning': return 'status-planning';
      case 'ongoing': return 'status-ongoing';
      case 'completed': return 'status-completed';
      default: return '';
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="detail-page-wrapper">
      {/* Animated Background Glows */}
      <div className="detail-bg-glow detail-bg-glow-1"></div>
      <div className="detail-bg-glow detail-bg-glow-2"></div>

      <div className="detail-container">
        {/* Back Button */}
        <button className="detail-back-btn" onClick={goBack}>
          ← Back
        </button>

        {/* Event Detail Card */}
        <div className="detail-card">
          {/* Image Section */}
          <div className="detail-image-section">
            <img 
              src={eventImages[event.image] || 'https://via.placeholder.com/1200x500?text=Sports+Event'} 
              alt={event.title}
              loading="lazy" // ✅ Lazy Loading Added
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x500?text=Sports+Event';
              }}
            />
            <div className="detail-image-overlay"></div>
            <div className="detail-badges">
              <span className={`detail-status ${getStatusClass(event.status)}`}>
                {getStatusText(event.status)}
              </span>
              <span className="detail-sport">{event.icon} {event.sport}</span>
            </div>
            {isFull && (
              <span className="detail-full-badge">🔴 Fully Booked</span>
            )}
          </div>

          {/* Content Section */}
          <div className="detail-content">
            <div className="detail-header">
              <h1 className="detail-title">{event.title}</h1>
              <div className="detail-category">
                🏷️ {event.category}
              </div>
            </div>

            <p className="detail-description">{event.description}</p>

            {/* Info Grid */}
            <div className="detail-info-grid">
              <div className="detail-info-item">
                <span className="detail-info-icon">📅</span>
                <div>
                  <span className="detail-info-label">Date</span>
                  <span className="detail-info-value">{event.date}</span>
                </div>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-icon">⏰</span>
                <div>
                  <span className="detail-info-label">Time</span>
                  <span className="detail-info-value">{event.time}</span>
                </div>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-icon">📍</span>
                <div>
                  <span className="detail-info-label">Venue</span>
                  <span className="detail-info-value">{event.venue}</span>
                </div>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-icon">💰</span>
                <div>
                  <span className="detail-info-label">Fee</span>
                  <span className="detail-info-value">{event.fee}</span>
                </div>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-icon">👥</span>
                <div>
                  <span className="detail-info-label">Max Teams</span>
                  <span className="detail-info-value">{event.maxTeams}</span>
                </div>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-icon">📋</span>
                <div>
                  <span className="detail-info-label">Booked</span>
                  <span className="detail-info-value">{event.bookedTeams}</span>
                </div>
              </div>
            </div>

            {/* Slots Section */}
            <div className="detail-slots">
              <div className="detail-slot-header">
                <span>Team Slots</span>
                <span className="detail-slot-count">{slotText}</span>
              </div>
              <div className="detail-slot-progress">
                <div 
                  className="detail-slot-progress-bar" 
                  style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
              </div>
              <div className="detail-slot-availability">
                {isFull ? (
                  <span className="detail-slot-full">🔴 Fully Booked</span>
                ) : bookingOpen ? (
                  <span className="detail-slot-available">
                    ✅ {event.maxTeams - bookedCount} slots available
                  </span>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>📌 Booking will open soon</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="detail-actions">
              {event.status === 'upcoming' && bookingOpen && !isFull ? (
                <button className="detail-register-btn" onClick={handleRegister}>
                  🎯 Register Your Team Now
                </button>
              ) : event.status === 'upcoming' && isFull ? (
                <div className="detail-action-message full">
                  <span>🔴</span>
                  <p>This event is fully booked. Please check other events.</p>
                </div>
              ) : event.status === 'planning' ? (
                <div className="detail-action-message completed">
                  <span>📌</span>
                  <p>This event is in planning stage. Registration will open soon.</p>
                </div>
              ) : event.status === 'completed' ? (
                <div className="detail-action-message completed">
                  <span>📌</span>
                  <p>This event has been completed.</p>
                </div>
              ) : null}
              
              <Link to="/events" className="detail-browse-btn">
                Browse More Events →
              </Link>
            </div>

            {/* Additional Info */}
            <div className="detail-extra">
              <h3>📋 Important Information</h3>
              <ul>
                <li>All team members must be registered participants</li>
                <li>Payment must be completed before the registration deadline</li>
                <li>Team captains will receive confirmation via email</li>
                <li>Please arrive at the venue 30 minutes before the scheduled time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail;